import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Sliders,
  Play,
  Video,
  CheckCircle2,
  Clock,
  AlertCircle,
  Volume2,
  RefreshCw,
  Sparkles,
  RotateCcw,
  Layers,
  HelpCircle,
  Film,
  Download,
  AlertTriangle,
  Monitor,
} from 'lucide-react';
import { api } from '../services/api.js';
import { Lesson, Scene, LessonSceneDefaults, DEFAULT_LESSON_SCENE_DEFAULTS, resolveSceneSettings } from '../types/index.js';
import { formatMediaUrl } from '../utils/media.js';
import { SceneCarousel } from '../components/SceneCarousel.js';
import { EduModal } from '../components/EduModal.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { EduConfirmDialog } from '../components/EduConfirmDialog.js';

type SaveStatus = 'saved' | 'saving' | 'pending' | 'error';

export const LessonEditor: React.FC = () => {
  const { id: lessonId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Core Data
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [selectedSceneId, setSelectedSceneId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Autosave State
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [lastSavedTime, setLastSavedTime] = useState<Date>(new Date());
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingChangesRef = useRef<{ sceneId: string; data: any } | null>(null);

  // Form inputs for current selected scene
  const [narrationText, setNarrationText] = useState('');
  const [transitionText, setTransitionText] = useState('');
  const [transitionPosition, setTransitionPosition] = useState<'none' | 'before' | 'after'>('none');
  const [startDelay, setStartDelay] = useState<string>('1.5');
  const [transitionDelay, setTransitionDelay] = useState<string>('0.5');
  const [endDelay, setEndDelay] = useState<string>('1.0');
  const [speechRate, setSpeechRate] = useState<string>('1.0');
  const [voiceId, setVoiceId] = useState<string>('faber');

  // Modals
  const [isDefaultsModalOpen, setIsDefaultsModalOpen] = useState(false);
  const [isApplyConfirmOpen, setIsApplyConfirmOpen] = useState(false);
  const [overwriteCustomSelection, setOverwriteCustomSelection] = useState(false);
  const [isLessonPreviewModalOpen, setIsLessonPreviewModalOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<'interactive' | 'rendered'>('interactive');
  const [isRenderConfirmOpen, setIsRenderConfirmOpen] = useState(false);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [renderingLoading, setRenderingLoading] = useState(false);

  // Preview Data
  const [lessonPreviewData, setLessonPreviewData] = useState<any>(null);
  const [isPlayingSceneAudio, setIsPlayingSceneAudio] = useState(false);
  const [sceneAudioLoading, setSceneAudioLoading] = useState(false);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);

  // Defaults Form
  const [tempDefaults, setTempDefaults] = useState<LessonSceneDefaults>({ ...DEFAULT_LESSON_SCENE_DEFAULTS });
  const [savingDefaults, setSavingDefaults] = useState(false);

  // 1. Initial Load
  const loadLessonData = useCallback(async () => {
    if (!lessonId) return;
    try {
      setLoading(true);
      setError(null);

      const [loadedLesson, loadedScenesRes] = await Promise.all([
        api.getLesson(lessonId),
        api.getScenes(lessonId),
      ]);

      setLesson(loadedLesson);
      setTempDefaults(loadedLesson.sceneDefaults || { ...DEFAULT_LESSON_SCENE_DEFAULTS });

      const sortedScenes = (loadedScenesRes.items || []).sort((a, b) => a.order - b.order);
      setScenes(sortedScenes);

      if (sortedScenes.length > 0) {
        setSelectedSceneId(sortedScenes[0].id);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados da videoaula.');
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    loadLessonData();
  }, [loadLessonData]);

  // Current active scene
  const currentScene = scenes.find((s) => s.id === selectedSceneId) || scenes[0];

  // Validation: Every scene must have both narration/script and image
  const incompleteScenes = scenes
    .map((sc, i) => {
      const sceneNum = sc.sceneNumber || sc.order || i + 1;
      const hasScript = Boolean((sc.narration || sc.text || '').trim());
      const hasImage = Boolean((sc.imagePath || sc.imageUrl || '').trim());
      return {
        scene: sc,
        sceneNum,
        missingScript: !hasScript,
        missingImage: !hasImage,
        hasIssues: !hasScript || !hasImage,
      };
    })
    .filter((s) => s.hasIssues);

  const canRender = scenes.length > 0 && incompleteScenes.length === 0;

  // Effective settings for current active scene
  const effectiveSettings = lesson && currentScene
    ? resolveSceneSettings(lesson.sceneDefaults, currentScene.overrides)
    : DEFAULT_LESSON_SCENE_DEFAULTS;

  // Sync scene data to local form inputs when active scene changes
  useEffect(() => {
    if (!currentScene || !lesson) return;

    // Flush any pending changes of previous scene first
    flushPendingSave();

    const eff = resolveSceneSettings(lesson.sceneDefaults, currentScene.overrides);
    setNarrationText(currentScene.narration || currentScene.text || '');
    setTransitionText(eff.transitionText || '');
    setTransitionPosition(eff.transitionPosition || 'none');
    setStartDelay(String(eff.startDelaySeconds ?? 1.5));
    setTransitionDelay(String(eff.transitionDelaySeconds ?? 0.5));
    setEndDelay(String(eff.endDelaySeconds ?? 1.0));
    setSpeechRate(String(eff.speechRate ?? 1.0));
    setVoiceId(eff.voiceId || 'faber');
  }, [selectedSceneId, lesson]);

  // Autosave flush mechanism
  const flushPendingSave = async () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (pendingChangesRef.current) {
      const { sceneId, data } = pendingChangesRef.current;
      pendingChangesRef.current = null;
      try {
        setSaveStatus('saving');
        const updated = await api.updateScene(sceneId, data);
        setScenes((prev) => prev.map((s) => (s.id === sceneId ? updated : s)));
        setSaveStatus('saved');
        setLastSavedTime(new Date());
      } catch (err) {
        console.error('Erro no salvamento automático:', err);
        setSaveStatus('error');
      }
    }
  };

  // Warn before unload if changes are pending
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (saveStatus === 'pending' || saveStatus === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveStatus]);

  // Schedule an autosave with 800ms debounce
  const scheduleAutosave = (updatedFields: any) => {
    if (!currentScene) return;

    setSaveStatus('pending');
    pendingChangesRef.current = {
      sceneId: currentScene.id,
      data: {
        ...(pendingChangesRef.current?.data || {}),
        ...updatedFields,
      },
    };

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      await flushPendingSave();
    }, 800);
  };

  // Handlers for individual field edits
  const handleNarrationChange = (val: string) => {
    setNarrationText(val);
    setScenes((prev) =>
      prev.map((s) =>
        s.id === currentScene.id
          ? { ...s, narration: val, text: val, wordCount: val.trim().split(/\s+/).filter(Boolean).length }
          : s
      )
    );
    scheduleAutosave({ narration: val });
  };

  const handleOverrideChange = (field: keyof LessonSceneDefaults, value: any) => {
    const updatedOverrides = {
      ...(currentScene.overrides || {}),
      [field]: value,
    };

    setScenes((prev) =>
      prev.map((s) =>
        s.id === currentScene.id ? { ...s, overrides: updatedOverrides } : s
      )
    );

    scheduleAutosave({ overrides: updatedOverrides });
  };

  const handleRestoreFieldDefault = (field: keyof LessonSceneDefaults) => {
    if (!currentScene || !lesson) return;

    const newOverrides = { ...(currentScene.overrides || {}) };
    delete newOverrides[field];

    setScenes((prev) =>
      prev.map((s) => (s.id === currentScene.id ? { ...s, overrides: newOverrides } : s))
    );

    // Reset local state to lesson default
    const lessonVal = lesson.sceneDefaults[field];
    if (field === 'startDelaySeconds') setStartDelay(String(lessonVal));
    if (field === 'endDelaySeconds') setEndDelay(String(lessonVal));
    if (field === 'transitionDelaySeconds') setTransitionDelay(String(lessonVal));
    if (field === 'transitionText') setTransitionText(String(lessonVal || ''));
    if (field === 'transitionPosition') setTransitionPosition((lessonVal as any) || 'none');
    if (field === 'speechRate') setSpeechRate(String(lessonVal));
    if (field === 'voiceId') setVoiceId(String(lessonVal || 'faber'));

    scheduleAutosave({ resetField: field });
  };

  const handleRestoreAllSceneDefaults = () => {
    if (!currentScene || !lesson) return;

    setScenes((prev) =>
      prev.map((s) => (s.id === currentScene.id ? { ...s, overrides: {} } : s))
    );

    const d = lesson.sceneDefaults;
    setStartDelay(String(d.startDelaySeconds));
    setEndDelay(String(d.endDelaySeconds));
    setTransitionDelay(String(d.transitionDelaySeconds));
    setTransitionText(d.transitionText || '');
    setTransitionPosition(d.transitionPosition || 'none');
    setSpeechRate(String(d.speechRate));
    setVoiceId(d.voiceId || 'faber');

    scheduleAutosave({ resetOverrides: true });
  };

  // Preview Scene Audio
  const handlePlayScenePreview = async () => {
    if (!currentScene) return;

    try {
      setSceneAudioLoading(true);
      await flushPendingSave();

      const previewRes = await api.audioPreviewScene(currentScene.id);

      // Play audio sequence: start delay -> audio -> end delay
      if (audioPlayerRef.current) {
        audioPlayerRef.current.src = previewRes.narrationAudioUrl;
        setIsPlayingSceneAudio(true);
        audioPlayerRef.current.play();
      }
    } catch (err: any) {
      alert(`Falha ao gerar áudio de prévia: ${err.message}`);
    } finally {
      setSceneAudioLoading(false);
    }
  };

  // Save Lesson Defaults
  const handleSaveLessonDefaults = async () => {
    if (!lesson) return;
    try {
      setSavingDefaults(true);
      const updated = await api.updateLessonDefaults(lesson.id, tempDefaults);
      setLesson(updated);
      setIsDefaultsModalOpen(false);
    } catch (err: any) {
      alert(`Erro ao salvar configurações padrão: ${err.message}`);
    } finally {
      setSavingDefaults(false);
    }
  };

  // Apply Defaults to All Scenes
  const handleApplyDefaultsToAll = async () => {
    if (!lesson) return;
    try {
      setSavingDefaults(true);
      const res = await api.applyLessonDefaults(lesson.id, overwriteCustomSelection);
      setIsApplyConfirmOpen(false);
      setIsDefaultsModalOpen(false);
      await loadLessonData();
      alert(res.message);
    } catch (err: any) {
      alert(`Erro ao aplicar configurações: ${err.message}`);
    } finally {
      setSavingDefaults(false);
    }
  };

  // Open Full Lesson Preview Modal
  const handleOpenLessonPreview = async () => {
    if (!lesson) return;
    try {
      const data = await api.getLessonPreview(lesson.id);
      setLessonPreviewData(data);
      if (data.renderedVideo && !data.hasNewerContentThanRender) {
        setPreviewMode('rendered');
      } else {
        setPreviewMode('interactive');
      }
      setIsLessonPreviewModalOpen(true);
    } catch (err: any) {
      alert(`Erro ao carregar prévia: ${err.message}`);
    }
  };

  // Request Render
  const handleConfirmRender = async () => {
    if (!lesson) return;
    if (!canRender) {
      setIsRenderConfirmOpen(false);
      setIsValidationModalOpen(true);
      return;
    }
    try {
      setRenderingLoading(true);
      await flushPendingSave();
      await api.renderLesson(lesson.id);
      setIsRenderConfirmOpen(false);
      navigate('/processamentos');
    } catch (err: any) {
      alert(`Falha ao solicitar renderização: ${err.message}`);
    } finally {
      setRenderingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <RefreshCw className="w-8 h-8 text-brand-primary animate-spin" />
        <p className="text-sm font-medium text-txt-secondary">Carregando editor de videoaula...</p>
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="p-8 max-w-2xl mx-auto text-center space-y-4">
        <div className="w-12 h-12 rounded-full bg-status-error/10 text-status-error flex items-center justify-center mx-auto">
          <AlertCircle className="w-6 h-6" />
        </div>
        <h2 className="text-xl font-bold text-txt-primary">Não foi possível abrir o editor</h2>
        <p className="text-sm text-txt-secondary">{error || 'Aula não encontrada.'}</p>
        <Link to="/aulas">
          <EduButton variant="secondary">Voltar para Minhas Aulas</EduButton>
        </Link>
      </div>
    );
  }

  const isCurrentSceneCustomized = currentScene && Object.keys(currentScene.overrides || {}).length > 0;
  const isFieldCustom = (field: keyof LessonSceneDefaults) =>
    Boolean(currentScene?.overrides && currentScene.overrides[field] !== undefined);

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-12">
      {/* Top Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-bdr px-6 py-3.5 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          {/* Left: Back & Title */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={async () => {
                await flushPendingSave();
                navigate(`/aulas?projectId=${lesson.projectId}`);
              }}
              className="p-1.5 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-surface-secondary transition-colors"
              title="Voltar para a lista de aulas"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-brand-primary uppercase tracking-wider">
                  EduRender — Editor de Aula
                </span>
                <span className="text-txt-muted text-xs">•</span>
                <span className="text-xs text-txt-muted truncate max-w-[200px]">{lesson.title}</span>
              </div>
              <h1 className="text-base font-bold text-txt-primary leading-tight">
                {lesson.title}
              </h1>
            </div>
          </div>

          {/* Center: Autosave Status */}
          <div className="flex items-center gap-2">
            {saveStatus === 'saved' && (
              <span className="inline-flex items-center gap-1.5 text-xs text-status-success bg-status-success/10 px-2.5 py-1 rounded-full font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Salvo</span>
              </span>
            )}
            {saveStatus === 'saving' && (
              <span className="inline-flex items-center gap-1.5 text-xs text-brand-primary bg-brand-light px-2.5 py-1 rounded-full font-medium">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>Salvando...</span>
              </span>
            )}
            {saveStatus === 'pending' && (
              <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full font-medium">
                <Clock className="w-3.5 h-3.5" />
                <span>Alterações pendentes</span>
              </span>
            )}
            {saveStatus === 'error' && (
              <button
                type="button"
                onClick={flushPendingSave}
                className="inline-flex items-center gap-1.5 text-xs text-status-error bg-status-error/10 px-2.5 py-1 rounded-full font-medium hover:bg-status-error/20"
              >
                <AlertCircle className="w-3.5 h-3.5" />
                <span>Erro ao salvar (Clique para tentar)</span>
              </button>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <EduButton
              variant="secondary"
              size="sm"
              icon={<Sliders size={14} />}
              onClick={() => setIsDefaultsModalOpen(true)}
            >
              Padrões da Aula
            </EduButton>

            <EduButton
              variant="secondary"
              size="sm"
              icon={<Play size={14} />}
              onClick={handleOpenLessonPreview}
            >
              Prévia da Aula
            </EduButton>

            <div className="relative inline-flex items-center">
              <EduButton
                variant={canRender ? "primary" : "secondary"}
                size="sm"
                icon={canRender ? <Sparkles size={14} /> : <AlertTriangle size={14} className="text-amber-600" />}
                onClick={() => {
                  if (!canRender) {
                    setIsValidationModalOpen(true);
                  } else {
                    setIsRenderConfirmOpen(true);
                  }
                }}
                className={!canRender ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100' : ''}
              >
                Gerar Videoaula
              </EduButton>
              {!canRender && (
                <span
                  title={`${incompleteScenes.length} cena(s) com pendências de roteiro ou imagem`}
                  className="absolute -top-1.5 -right-1.5 flex h-4 min-w-[16px] px-1 items-center justify-center rounded-full bg-amber-600 text-white text-[10px] font-bold shadow-xs pointer-events-none"
                >
                  {incompleteScenes.length}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Workspace */}
      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Render Validation Alert Banner if any scenes are incomplete */}
        {!canRender && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-amber-900">
                  {incompleteScenes.length === 1
                    ? '1 cena com pendência antes de permitir a renderização'
                    : `${incompleteScenes.length} cenas com pendências antes de permitir a renderização`}
                </h4>
                <p className="text-[11px] text-amber-700">
                  Para gerar o vídeo MP4 final, todas as cenas precisam ter roteiro de narração e slide/imagem definidos.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsValidationModalOpen(true)}
              className="text-xs font-bold text-amber-900 hover:text-amber-950 underline px-2 py-1"
            >
              Ver pendências
            </button>
          </div>
        )}

        {/* Row with Region A (Scene Visualizer) and Region B (Scene Form) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Região A: Visualização da Cena (5 cols) */}
          <div className="lg:col-span-5 bg-white border border-bdr rounded-2xl p-5 shadow-sm space-y-4">
            {/* Slide Header */}
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-txt-muted uppercase">
                  Cena {currentScene ? currentScene.sceneNumber || currentScene.order : 1} de {scenes.length}
                </span>
                <h3 className="text-sm font-bold text-txt-primary">
                  Slide {String(currentScene ? currentScene.slideNumber || currentScene.order : 1).padStart(2, '0')}
                </h3>
              </div>

              <div>
                {isCurrentSceneCustomized ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-brand-light text-brand-primary border border-brand-primary/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-primary" />
                    Edição local
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-surface-secondary text-txt-secondary border border-bdr">
                    Padrão da aula
                  </span>
                )}
              </div>
            </div>

            {/* 16:9 Slide Viewer */}
            <div className="relative w-full aspect-video rounded-xl bg-slate-900 border border-slate-800 overflow-hidden flex items-center justify-center shadow-inner group">
              {currentScene?.imageUrl ? (
                <img
                  src={formatMediaUrl(currentScene.imageUrl)}
                  alt={`Slide ${currentScene.slideNumber}`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="flex flex-col items-center justify-center p-6 text-center text-white/70 space-y-2">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-brand-primary">
                    <Monitor className="w-7 h-7" />
                  </div>
                  <h4 className="text-base font-semibold text-white">
                    Slide {String(currentScene ? currentScene.slideNumber || currentScene.order : 1).padStart(2, '0')}
                  </h4>
                  <p className="text-xs text-white/50 max-w-xs">
                    Prévia ilustrativa da imagem importada para a videoaula
                  </p>
                </div>
              )}

              {/* Bottom Overlay with Duration */}
              <div className="absolute bottom-2 right-2 bg-black/75 backdrop-blur-md px-2.5 py-1 rounded-md text-[11px] font-mono text-white flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-brand-primary" />
                <span>
                  {currentScene?.duration
                    ? `${Math.round(currentScene.duration)}s estimada`
                    : 'Aguardando estimativa'}
                </span>
              </div>
            </div>

            {/* Scene Controls & Actions under slide */}
            <div className="flex items-center justify-between pt-1 text-xs text-txt-secondary">
              <span className="text-txt-muted">
                Palavras: <strong>{currentScene?.wordCount || 0}</strong>
              </span>

              {isCurrentSceneCustomized && (
                <button
                  type="button"
                  onClick={handleRestoreAllSceneDefaults}
                  className="flex items-center gap-1 text-txt-secondary hover:text-brand-primary transition-colors"
                  title="Remove todas as personalizações desta cena e volta aos padrões da aula"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Restaurar padrão da aula</span>
                </button>
              )}
            </div>
          </div>

          {/* Região B: Painel de Configurações da Cena (7 cols) */}
          <div className="lg:col-span-7 bg-white border border-bdr rounded-2xl p-6 shadow-sm space-y-5">
            {/* Header da Região B */}
            <div className="flex items-center justify-between pb-3 border-b border-bdr">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-brand-primary" />
                <h2 className="text-sm font-bold text-txt-primary">Roteiro e Parâmetros da Cena</h2>
              </div>

              {/* Preview Button */}
              <button
                type="button"
                onClick={handlePlayScenePreview}
                disabled={sceneAudioLoading || !narrationText.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand-light text-brand-primary hover:bg-brand-primary hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {sceneAudioLoading ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Volume2 className="w-3.5 h-3.5" />
                )}
                <span>Prévia da Cena</span>
              </button>
            </div>

            {/* Hidden audio element for preview */}
            <audio
              ref={audioPlayerRef}
              onEnded={() => setIsPlayingSceneAudio(false)}
              className="hidden"
            />

            {/* Main Narration Textarea */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-txt-primary">
                  Roteiro de Narração Principal
                </label>
                <span className="text-[11px] text-txt-muted">
                  {narrationText.trim().split(/\s+/).filter(Boolean).length} palavras
                </span>
              </div>
              <textarea
                rows={4}
                value={narrationText}
                onChange={(e) => handleNarrationChange(e.target.value)}
                placeholder="Insira o texto que será narrado pelo Piper TTS para este slide..."
                className="w-full text-sm p-3 rounded-xl border border-bdr focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none transition resize-y leading-relaxed text-txt-primary bg-surface-secondary/40 focus:bg-white"
              />
            </div>

            {/* Transition Text (Mensagem de Ligação) */}
            <div className="space-y-2 p-3.5 rounded-xl bg-surface-secondary/50 border border-bdr">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-txt-primary flex items-center gap-1.5">
                  <span>Mensagem de Ligação</span>
                  <span className="text-[10px] text-txt-muted font-normal">(Opcional)</span>
                </label>

                {isFieldCustom('transitionText') && (
                  <button
                    type="button"
                    onClick={() => handleRestoreFieldDefault('transitionText')}
                    className="text-[10px] text-brand-primary hover:underline flex items-center gap-0.5"
                  >
                    <RotateCcw className="w-2.5 h-2.5" /> Restaurar padrão
                  </button>
                )}
              </div>

              <input
                type="text"
                value={transitionText}
                onChange={(e) => {
                  setTransitionText(e.target.value);
                  handleOverrideChange('transitionText', e.target.value);
                }}
                placeholder="Ex.: Agora, vamos ao próximo tópico."
                className="w-full text-xs p-2.5 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
              />

              {/* Transition Position Selector */}
              <div className="flex items-center gap-3 pt-1">
                <span className="text-xs text-txt-secondary font-medium">Posição:</span>
                <div className="flex gap-1.5">
                  {[
                    { id: 'none', label: 'Desativada' },
                    { id: 'before', label: 'Antes da narração' },
                    { id: 'after', label: 'Depois da narração' },
                  ].map((pos) => (
                    <button
                      key={pos.id}
                      type="button"
                      onClick={() => {
                        setTransitionPosition(pos.id as any);
                        handleOverrideChange('transitionPosition', pos.id);
                      }}
                      className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                        transitionPosition === pos.id
                          ? 'border-brand-primary bg-brand-light text-brand-primary font-semibold'
                          : 'border-bdr bg-white text-txt-secondary hover:bg-surface-secondary'
                      }`}
                    >
                      {pos.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Pausas & Delays (Pausa Inicial e Final) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pausa Inicial */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-txt-primary">Pausa inicial (s)</label>
                  {isFieldCustom('startDelaySeconds') ? (
                    <button
                      type="button"
                      onClick={() => handleRestoreFieldDefault('startDelaySeconds')}
                      className="text-[10px] text-brand-primary hover:underline"
                    >
                      Restaurar padrão
                    </button>
                  ) : null}
                </div>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={startDelay}
                  onChange={(e) => {
                    setStartDelay(e.target.value);
                    const parsed = parseFloat(e.target.value) || 0;
                    handleOverrideChange('startDelaySeconds', parsed);
                  }}
                  className="w-full text-xs p-2.5 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
                />
                <span className="text-[10px] text-txt-muted block">
                  {isFieldCustom('startDelaySeconds') ? (
                    <span className="text-brand-primary font-medium">• Personalizado nesta cena</span>
                  ) : (
                    'Herdado da aula'
                  )}
                </span>
              </div>

              {/* Pausa Final */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-txt-primary">Pausa final (s)</label>
                  {isFieldCustom('endDelaySeconds') ? (
                    <button
                      type="button"
                      onClick={() => handleRestoreFieldDefault('endDelaySeconds')}
                      className="text-[10px] text-brand-primary hover:underline"
                    >
                      Restaurar padrão
                    </button>
                  ) : null}
                </div>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  value={endDelay}
                  onChange={(e) => {
                    setEndDelay(e.target.value);
                    const parsed = parseFloat(e.target.value) || 0;
                    handleOverrideChange('endDelaySeconds', parsed);
                  }}
                  className="w-full text-xs p-2.5 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
                />
                <span className="text-[10px] text-txt-muted block">
                  {isFieldCustom('endDelaySeconds') ? (
                    <span className="text-brand-primary font-medium">• Personalizado nesta cena</span>
                  ) : (
                    'Herdado da aula'
                  )}
                </span>
              </div>
            </div>

            {/* Linha do Tempo Visual da Cena */}
            <div className="space-y-2 pt-2 border-t border-bdr">
              <label className="text-xs font-semibold text-txt-primary flex items-center justify-between">
                <span>Linha do Tempo da Cena</span>
                <span className="text-[10px] font-normal text-txt-muted">
                  Sequência cronológica da renderização
                </span>
              </label>

              <div className="w-full bg-slate-100 rounded-lg p-2.5 border border-bdr space-y-2">
                <div className="flex items-center gap-1.5 text-xs overflow-x-auto pb-1">
                  {/* Segment: Pausa Inicial */}
                  <div className="flex-shrink-0 bg-blue-100 text-blue-800 border border-blue-200 px-2 py-1 rounded text-center text-[10px] font-medium">
                    Início: {startDelay}s
                  </div>

                  <span className="text-slate-400">→</span>

                  {/* Segment: Ligação Before */}
                  {transitionPosition === 'before' && transitionText && (
                    <>
                      <div className="flex-shrink-0 bg-purple-100 text-purple-800 border border-purple-200 px-2 py-1 rounded text-center text-[10px] font-medium max-w-[120px] truncate">
                        Ligação: &quot;{transitionText}&quot;
                      </div>
                      <span className="text-slate-400">→</span>
                      <div className="flex-shrink-0 bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded text-[9px]">
                        Pausa: {transitionDelay}s
                      </div>
                      <span className="text-slate-400">→</span>
                    </>
                  )}

                  {/* Segment: Narração Principal */}
                  <div className="flex-grow bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-1 rounded text-center text-[10px] font-medium truncate">
                    Narração Principal (~{Math.round(currentScene?.wordCount || 0)} palavras)
                  </div>

                  {/* Segment: Ligação After */}
                  {transitionPosition === 'after' && transitionText && (
                    <>
                      <span className="text-slate-400">→</span>
                      <div className="flex-shrink-0 bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded text-[9px]">
                        Pausa: {transitionDelay}s
                      </div>
                      <span className="text-slate-400">→</span>
                      <div className="flex-shrink-0 bg-purple-100 text-purple-800 border border-purple-200 px-2 py-1 rounded text-center text-[10px] font-medium max-w-[120px] truncate">
                        Ligação: &quot;{transitionText}&quot;
                      </div>
                    </>
                  )}

                  <span className="text-slate-400">→</span>

                  {/* Segment: Pausa Final */}
                  <div className="flex-shrink-0 bg-blue-100 text-blue-800 border border-blue-200 px-2 py-1 rounded text-center text-[10px] font-medium">
                    Fim: {endDelay}s
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Região C: Carrossel Horizontal de Cenas */}
        <div className="w-full">
          <SceneCarousel
            scenes={scenes}
            selectedSceneId={selectedSceneId}
            onSelectScene={(id) => setSelectedSceneId(id)}
          />
        </div>
      </main>

      {/* Modal: Padrões da Aula */}
      <EduModal
        isOpen={isDefaultsModalOpen}
        onClose={() => setIsDefaultsModalOpen(false)}
        title="Configurações Padrão da Aula"
        maxWidth="lg"
      >
        <div className="space-y-5">
          <p className="text-xs text-txt-secondary">
            Estas configurações servem como base padrão para todas as cenas desta aula. Cenas que possuírem ajustes individuais manterão suas personalizações, exceto se você escolher substituí-las.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Voz Padrão */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-txt-primary">Voz Padrão</label>
              <select
                value={tempDefaults.voiceId || 'faber'}
                onChange={(e) => setTempDefaults({ ...tempDefaults, voiceId: e.target.value })}
                className="w-full text-xs p-2 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
              >
                <option value="faber">Faber (Voz Oficial EduRender - Português BR)</option>
              </select>
            </div>

            {/* Velocidade de Narração */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-txt-primary">Velocidade da Voz</label>
              <select
                value={tempDefaults.speechRate}
                onChange={(e) => setTempDefaults({ ...tempDefaults, speechRate: parseFloat(e.target.value) })}
                className="w-full text-xs p-2 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
              >
                <option value="0.9">0.9x (Pausada)</option>
                <option value="1.0">1.0x (Padrão natural)</option>
                <option value="1.1">1.1x (Dinâmica)</option>
                <option value="1.2">1.2x (Acelerada)</option>
              </select>
            </div>

            {/* Pausa Inicial Padrão */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-txt-primary">Pausa Inicial Padrão (s)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="10"
                value={tempDefaults.startDelaySeconds}
                onChange={(e) => setTempDefaults({ ...tempDefaults, startDelaySeconds: parseFloat(e.target.value) || 0 })}
                className="w-full text-xs p-2 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
              />
            </div>

            {/* Pausa Final Padrão */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-txt-primary">Pausa Final Padrão (s)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="10"
                value={tempDefaults.endDelaySeconds}
                onChange={(e) => setTempDefaults({ ...tempDefaults, endDelaySeconds: parseFloat(e.target.value) || 0 })}
                className="w-full text-xs p-2 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
              />
            </div>

            {/* Resolução de Saída */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-txt-primary">Resolução do Vídeo</label>
              <select
                value={`${tempDefaults.outputWidth}x${tempDefaults.outputHeight}`}
                onChange={(e) => {
                  const [w, h] = e.target.value.split('x').map(Number);
                  setTempDefaults({ ...tempDefaults, outputWidth: w, outputHeight: h });
                }}
                className="w-full text-xs p-2 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
              >
                <option value="1280x720">720p HD (1280x720) — Recomendado</option>
                <option value="1920x1080">1080p Full HD (1920x1080)</option>
              </select>
            </div>

            {/* Transição Visual */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-txt-primary">Transição Visual entre Cenas</label>
              <select
                value={tempDefaults.visualTransition}
                onChange={(e) => setTempDefaults({ ...tempDefaults, visualTransition: e.target.value as any })}
                className="w-full text-xs p-2 rounded-lg border border-bdr bg-white focus:border-brand-primary outline-none"
              >
                <option value="cut">Corte Seco (Padrão)</option>
                <option value="fade">Esmaecimento Suave (Fade)</option>
              </select>
            </div>
          </div>

          <div className="pt-4 border-t border-bdr flex items-center justify-between">
            <button
              type="button"
              onClick={() => setIsApplyConfirmOpen(true)}
              className="text-xs font-semibold text-brand-primary hover:text-brand-hover underline"
            >
              Aplicar a todas as cenas...
            </button>

            <div className="flex gap-2">
              <EduButton variant="secondary" size="sm" onClick={() => setIsDefaultsModalOpen(false)}>
                Cancelar
              </EduButton>
              <EduButton variant="primary" size="sm" onClick={handleSaveLessonDefaults} isLoading={savingDefaults}>
                Salvar Padrões da Aula
              </EduButton>
            </div>
          </div>
        </div>
      </EduModal>

      {/* Modal: Aplicar a Todas as Cenas */}
      <EduModal
        isOpen={isApplyConfirmOpen}
        onClose={() => setIsApplyConfirmOpen(false)}
        title="Aplicar Configurações a Todas as Cenas"
        maxWidth="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-txt-primary">
            Você está prestes a aplicar os padrões da aula a todas as <strong>{scenes.length} cenas</strong> desta videoaula.
          </p>

          <div className="space-y-3 p-3.5 rounded-xl bg-surface-secondary border border-bdr text-xs">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="radio"
                name="applyMode"
                checked={!overwriteCustomSelection}
                onChange={() => setOverwriteCustomSelection(false)}
                className="mt-0.5 text-brand-primary focus:ring-brand-primary"
              />
              <div>
                <strong className="text-txt-primary block">Preservar personalizações existentes (Recomendado)</strong>
                <span className="text-txt-muted text-[11px]">
                  Apenas cenas que utilizam o padrão da aula serão atualizadas. Cenas que possuem pausas ou roteiros específicos não serão modificadas.
                </span>
              </div>
            </label>

            <label className="flex items-start gap-2.5 cursor-pointer pt-2 border-t border-bdr">
              <input
                type="radio"
                name="applyMode"
                checked={overwriteCustomSelection}
                onChange={() => setOverwriteCustomSelection(true)}
                className="mt-0.5 text-brand-primary focus:ring-brand-primary"
              />
              <div>
                <strong className="text-txt-primary block">Substituir personalizações existentes</strong>
                <span className="text-txt-muted text-[11px]">
                  Todos os ajustes individuais de pausas e transições serão redefinidos para os valores padrão da aula.
                </span>
              </div>
            </label>
          </div>

          <div className="pt-3 flex justify-end gap-2">
            <EduButton variant="secondary" size="sm" onClick={() => setIsApplyConfirmOpen(false)}>
              Voltar
            </EduButton>
            <EduButton variant="primary" size="sm" onClick={handleApplyDefaultsToAll} isLoading={savingDefaults}>
              Confirmar e Aplicar
            </EduButton>
          </div>
        </div>
      </EduModal>

      {/* Modal: Prévia da Aula (Modo A & B) */}
      <EduModal
        isOpen={isLessonPreviewModalOpen}
        onClose={() => setIsLessonPreviewModalOpen(false)}
        title="Prévia da Videoaula"
        maxWidth="xl"
      >
        <div className="space-y-4">
          {/* Mode Switcher */}
          <div className="flex items-center justify-between border-b border-bdr pb-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPreviewMode('interactive')}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                  previewMode === 'interactive'
                    ? 'bg-brand-primary text-white font-semibold'
                    : 'bg-surface-secondary text-txt-secondary hover:bg-slate-200'
                }`}
              >
                Modo A — Sequência de Slides & Narrações
              </button>

              <button
                type="button"
                onClick={() => setPreviewMode('rendered')}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                  previewMode === 'rendered'
                    ? 'bg-brand-primary text-white font-semibold'
                    : 'bg-surface-secondary text-txt-secondary hover:bg-slate-200'
                }`}
              >
                Modo B — Vídeo Final Renderizado
              </button>
            </div>

            {lessonPreviewData?.hasNewerContentThanRender && (
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>Existe versão mais recente não renderizada</span>
              </span>
            )}
          </div>

          {/* Mode A: Interactive Sequence */}
          {previewMode === 'interactive' && (
            <div className="space-y-4">
              <p className="text-xs text-txt-secondary">
                Neste modo, você pode revisar cada cena em sequência com seus respectivos slides, pausas e narrações sem necessidade de renderizar o arquivo final MP4.
              </p>

              <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-1">
                {scenes.map((sc, idx) => (
                  <div
                    key={sc.id}
                    className="flex items-center gap-3 p-3 rounded-xl border border-bdr bg-white hover:border-brand-primary/40 transition-colors"
                  >
                    <div className="w-20 aspect-video rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 border border-bdr">
                      {sc.imageUrl ? (
                        <img src={formatMediaUrl(sc.imageUrl)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex items-center justify-center h-full text-slate-400">
                          <Monitor className="w-4 h-4" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-txt-primary">
                          Cena {String(idx + 1).padStart(2, '0')}
                        </span>
                        {sc.duration ? (
                          <span className="text-[10px] text-txt-muted">
                            (~{Math.round(sc.duration)}s)
                          </span>
                        ) : null}

                        {!((sc.narration || sc.text || '').trim()) && (
                          <span className="text-[10px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.2 rounded">
                            Sem Roteiro
                          </span>
                        )}
                        {!((sc.imageUrl || sc.imagePath || '').trim()) && (
                          <span className="text-[10px] font-bold text-rose-800 bg-rose-100 px-1.5 py-0.2 rounded">
                            Sem Imagem
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-txt-secondary truncate mt-0.5">
                        {(sc.narration || sc.text || '').trim() || (
                          <span className="text-amber-700 italic">Roteiro pendente de preenchimento</span>
                        )}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setIsLessonPreviewModalOpen(false);
                        setSelectedSceneId(sc.id);
                      }}
                      className="text-xs font-semibold text-brand-primary hover:underline px-2"
                    >
                      Editar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mode B: Rendered Video Player */}
          {previewMode === 'rendered' && (
            <div className="space-y-4">
              {lessonPreviewData?.renderedVideo ? (
                <div className="space-y-3">
                  <div className="w-full aspect-video rounded-xl bg-black overflow-hidden shadow-lg">
                    <video
                      controls
                      src={formatMediaUrl(lessonPreviewData.renderedVideo.videoUrl)}
                      className="w-full h-full"
                    />
                  </div>

                  <div className="flex items-center justify-between text-xs text-txt-secondary">
                    <span>
                      Renderizado em: <strong>{new Date(lessonPreviewData.renderedVideo.createdAt).toLocaleString()}</strong>
                    </span>
                    <a
                      href={formatMediaUrl(lessonPreviewData.renderedVideo.videoUrl)}
                      download="videoaula.mp4"
                      className="flex items-center gap-1.5 text-brand-primary font-semibold hover:underline"
                    >
                      <Download className="w-4 h-4" />
                      <span>Baixar MP4</span>
                    </a>
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center space-y-3 bg-surface-secondary rounded-xl border border-bdr">
                  <Film className="w-8 h-8 text-txt-muted mx-auto" />
                  <h4 className="text-sm font-bold text-txt-primary">Nenhum vídeo renderizado ainda</h4>
                  <p className="text-xs text-txt-muted max-w-sm mx-auto">
                    Você pode iniciar a produção clicando em &quot;Gerar Videoaula&quot; no cabeçalho do editor.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </EduModal>

      {/* Modal: Confirmar Renderização */}
      <EduConfirmDialog
        isOpen={isRenderConfirmOpen}
        onClose={() => setIsRenderConfirmOpen(false)}
        onConfirm={handleConfirmRender}
        title="Iniciar Renderização da Videoaula"
        description={`Esta ação irá colocar a aula "${lesson.title}" (${scenes.length} cenas) na fila de processamento do Worker Linux na DigitalOcean. Uma franquia mensal de videoaula será reservada.`}
        confirmLabel="Confirmar e Iniciar Produção"
        variant="warning"
        isLoading={renderingLoading}
      />

      {/* Modal: Pendências para Renderização */}
      <EduModal
        isOpen={isValidationModalOpen}
        onClose={() => setIsValidationModalOpen(false)}
        title="Pendências para Renderização da Videoaula"
        maxWidth="md"
      >
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2.5 text-xs text-amber-900">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <span>
              Para garantir uma videoaula completa e de alta qualidade, <strong>todas as cenas precisam ter roteiro e imagem</strong> antes de iniciar a produção.
            </span>
          </div>

          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {incompleteScenes.map((item) => (
              <div
                key={item.scene.id}
                className="flex items-center justify-between p-3 bg-surface-secondary border border-bdr rounded-lg hover:border-gray-300 transition-colors"
              >
                <div>
                  <h5 className="text-xs font-bold text-txt-primary">
                    Cena {String(item.sceneNum).padStart(2, '0')}
                  </h5>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {item.missingScript && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                        Sem Roteiro
                      </span>
                    )}
                    {item.missingImage && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded">
                        Sem Imagem / Slide
                      </span>
                    )}
                  </div>
                </div>

                <EduButton
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setSelectedSceneId(item.scene.id);
                    setIsValidationModalOpen(false);
                  }}
                >
                  Editar Cena
                </EduButton>
              </div>
            ))}
          </div>

          <div className="pt-2 border-t border-bdr flex justify-end">
            <EduButton
              variant="secondary"
              size="sm"
              onClick={() => setIsValidationModalOpen(false)}
            >
              Entendido
            </EduButton>
          </div>
        </div>
      </EduModal>
    </div>
  );
};
